import { createTheme, Theme } from "@mui/material"
import { BIDGELY_TYPOGRAPHY } from "./typography"
import { RunContextApp } from "framework/RCApp";
import { getStudioTokens } from './theme-tokens';

export function getTheme(rc: RunContextApp, mode: 'light' | 'dark' = 'light'): Theme {
  const isDark = mode === 'dark';
  const tokens = getStudioTokens(mode);
  let theme: Theme = createTheme({
    typography: {
      fontFamily: [
        "Inter",
        "Inter Fallback",
        'Arial',
        'sans-serif',
      ].join(','),
    },
    components: {
      // Name of the component
      MuiButtonBase: {
        defaultProps: {
          // The props to change the default for.
          disableRipple: true, // No more ripple, on the whole application 💣!
        },
        styleOverrides: {
          root: {
            fontFamily: '"Inter", "Inter Fallback", Arial, sans-serif !important',
            '&.Mui-disabled' : {
              color: tokens.action.disabled + '80 !important',
            }
          }
        },
      },
    },
  });

  theme = createTheme(theme, {
    palette: {
      mode,
      primary: {
        main: tokens.primary.main,
        light: tokens.primary.light,
        dark: tokens.primary.dark,
        contrastText: tokens.primary.contrastText,
      },
      secondary: {
        main: tokens.secondary.main,
        light: tokens.secondary.light,
        dark: tokens.secondary.dark,
        contrastText: tokens.secondary.contrastText,
      },
      error: {
        main: isDark ? '#f44336' : '#d32f2f',         // Red
        light: isDark ? '#ff7961' : '#ef5350',        // Lighter red
        dark: isDark ? '#ba000d' : '#c62828',         // Darker red
        contrastText: '#ffffff',
      },
      warning: {
        main: isDark ? '#ff9800' : '#ed6c02',         // Orange
        light: isDark ? '#ffb74d' : '#ff9800',        // Lighter orange
        dark: isDark ? '#f57c00' : '#e65100',         // Darker orange
        contrastText: '#ffffff',
      },
      success: {
        main: isDark ? '#4caf50' : '#2e7d32',         // Green
        light: isDark ? '#81c784' : '#4caf50',        // Lighter green
        dark: isDark ? '#388e3c' : '#1b5e20',         // Darker green
        contrastText: '#ffffff',
      },
      info: {
        main: isDark ? '#2196f3' : '#0288d1',         // Blue
        light: isDark ? '#64b5f6' : '#29b6f6',        // Lighter blue
        dark: isDark ? '#1976d2' : '#01579b',         // Darker blue
        contrastText: '#ffffff',
      },
      background: {
        default: tokens.background.default,
        paper: tokens.background.paper,
      },
      text: {
        primary: tokens.text.primary,
        secondary: tokens.text.secondary,
      },
      DataGrid: {
        // Container background
        bg: tokens.background.paper,
      },
      divider: isDark ? '#143352' : tokens.border,
      action: {
        disabled: tokens.action.disabled,
        disabledBackground: tokens.action.disabledBackground,
      },
      button: {
        color: tokens.text.secondary,
        disabledBorder: isDark ? '#444c56' : '#ccc',
        disabledColor: isDark ? '#A1A1A1' : '#787878',
        disabledBackground: isDark ? '#0A1A29' : '#f2f2f2',
      }
    },
    components: {
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: tokens.background.paper,
            color: isDark ? '#e5e9f0' : '#334155',
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          html: {
            height: '100%'
          },
          body: {
            height: '100%',
            backgroundColor: tokens.background.default,
            color: tokens.text.primary,
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
            textRendering: 'optimizeLegibility',
            fontFamily: '"Inter", "Inter Fallback", Arial, sans-serif',
            fontSize: '14px',
            lineHeight: 1.5,
          },
          '*': {
            fontFamily: '"Inter", "Inter Fallback", Arial, sans-serif',
          }
        }
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: 8,
            fontWeight: 500,
            fontSize: 14,
            gap: 8,
            minHeight: 40,
            paddingTop: 0,
            paddingBottom: 0,
            paddingLeft: 16,
            paddingRight: 16,
            '&:focus-visible': {
              outline: '2px solid transparent',
              outlineOffset: 0,
              boxShadow: `0 0 0 2px ${tokens.background.paper}, 0 0 0 4px ${tokens.ring}`,
            },
            '&.Mui-disabled': {
              // Ensure borders are visible in disabled state
              border: '1px solid',
              borderColor: tokens.border,
            },
          },
          sizeSmall: {
            minHeight: 36,
            paddingLeft: 12,
            paddingRight: 12,
          },
          sizeLarge: {
            minHeight: 44,
            paddingLeft: 16,
            paddingRight: 16,
          },
          containedPrimary: {
            '&:hover': {
              backgroundColor: tokens.primary.light,
            },
          },
          outlinedPrimary: {
            borderColor: tokens.primary.main,
            backgroundColor: tokens.background.default,
            '&:hover': {
              backgroundColor: `${tokens.buttonPrimaryHover.main}`,
              borderColor: tokens.buttonPrimaryHover.main,
              color: tokens.primary.contrastText
            },
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderColor: tokens.sidebar.border,
            backgroundColor: tokens.sidebar.background,
            color: tokens.sidebar.foreground,
            '& .MuiListItem-root': {
              '&.Mui-selected': {
                backgroundColor: tokens.sidebar.primary,
                color: tokens.sidebar.primaryForeground,
                '&:hover': {
                  backgroundColor: tokens.sidebar.primary,
                },
              },
              '&:hover': {
                backgroundColor: tokens.sidebar.accent,
                color: tokens.sidebar.accentForeground,
              },
            },
            '& .MuiDivider-root': {
              borderColor: tokens.sidebar.border,
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: ({ theme }: { theme: any }) => ({
            backgroundColor: isDark ? '#0c1f31' : tokens.background.paper,
            color: isDark ? '#e5e9f0' : '#334155',
            border: `1px solid ${theme.palette.secondary.dark}`,
            borderColor: isDark ? '#143352' : tokens.border,
          }),
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: ({ theme, ownerState }: { theme: any; ownerState: any }) => ({
            fontFamily: '"Inter", "Inter Fallback", Arial, sans-serif !important',
            // Only apply default color to body text variants, let headings use their theme colors
            ...(ownerState?.variant?.includes('bglBody') || ownerState?.variant?.includes('body') || ownerState?.variant?.includes('caption') ? {
              color: tokens.text.primary,
            } : {}),
          }),
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-head': {
              color: isDark ? '#ffffff' :'#6c7f93',
              backgroundColor: isDark ? '#0c1f31' : '#ffffff',
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            color: isDark ? '#ffffff' :'#6c7f93',
            borderColor: isDark ? '#222c3a' : '#dedede',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            color: isDark ? '#ffffff' :'#6c7f93',
            backgroundColor: isDark ? '#2a3a4d' : '#f2f2f2',
            borderColor: isDark ? '#222c3a' : '#dedede',
          },
          colorSuccess: {
            backgroundColor: isDark ? '#176b43' : theme.palette.success.main,
            color: '#fff',
          },
          colorWarning: {
            backgroundColor: isDark ? '#7c4a03' : theme.palette.warning.main,
            color: '#fff',
          },
          colorInfo: {
            backgroundColor: isDark ? '#174ea6' : theme.palette.info.main,
            color: '#fff',
          },
          colorError: {
            backgroundColor: isDark ? '#a31515' : theme.palette.error.main,
            color: '#fff',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? '#0c1f31' : '#ffffff',
            color: isDark ? '#ffffff' :'#6c7f93',
            // Remove any overlay effects or gradients
            backgroundImage: 'none',
            '--Paper-overlay': 'none',
            '--Paper-shadow': 'none',
            '&::before': {
              display: 'none',
            },
            '&::after': {
              display: 'none',
            },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: isDark ? '#0c1f31' : '#ffffff',
            color: isDark ? '#ffffff' :'#6c7f93',
            // Remove any overlay effects or gradients
            backgroundImage: 'none',
            '--Paper-overlay': 'none',
            '--Paper-shadow': 'none',
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: isDark ? '#0c1f31' : '#ffffff',
            color: isDark ? '#ffffff' :'#6c7f93',
            // Remove any overlay effects or gradients
            backgroundImage: 'none',
            '--Paper-overlay': 'none',
            '--Paper-shadow': 'none',
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            color: isDark ? '#ffffff' :'#6c7f93',
            '&:hover': {
              backgroundColor: isDark ? '#2a3a4d' : '#f5f5f5',
            },
            minHeight: 36,
            paddingTop: 8,
            paddingBottom: 8,
            paddingLeft: 16,
            paddingRight: 16,
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            color: tokens.sidebar.foreground,
            borderRadius: 4,
            paddingTop: 6,
            paddingBottom: 6,
            '&:hover': {
              backgroundColor: tokens.sidebar.accent,
              color: tokens.sidebar.accentForeground,
            },
            '&.Mui-selected': {
              backgroundColor: '#0b2641',
              color: tokens.sidebar.primaryForeground,
            },
            '&.Mui-disabled': {
              color: tokens.sidebar.foreground,
              opacity: 1,
              cursor: 'not-allowed',
              backgroundColor: 'transparent',
            },
          },
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            color: tokens.sidebar.foreground,
            minWidth: 30,
            '& .MuiSvgIcon-root': {
              fontSize: 20,
            },
          },
        },
      },
      // DataGrid specific overrides
      MuiDataGrid: {
        styleOverrides: {
          root: {
            backgroundColor: tokens.background.paper,
            color: tokens.text.primary,
            borderColor: tokens.border,
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: tokens.background.paper,
              color: tokens.text.primary,
              borderColor: tokens.border,
            },
            '& .MuiDataGrid-columnHeader': {
              backgroundColor: tokens.background.paper,
              color: tokens.text.primary,
              borderColor: tokens.border,
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              color: tokens.text.primary,
            },
            '& .MuiDataGrid-cell': {
              backgroundColor: tokens.background.paper,
              color: tokens.text.primary,
              borderColor: tokens.border,
            },
            '& .MuiDataGrid-row': {
              backgroundColor: tokens.background.paper,
              color: tokens.text.primary,
              '&:hover': {
                backgroundColor: isDark ? '#2a3a4d' : '#f5f5f5',
              },
            },
            '& .MuiDataGrid-footerContainer': {
              backgroundColor: tokens.background.paper,
              color: tokens.text.primary,
              borderColor: tokens.border,
            },
            '& .MuiDataGrid-virtualScroller': {
              backgroundColor: tokens.background.paper,
            },
            '& .MuiDataGrid-virtualScrollerContent': {
              backgroundColor: tokens.background.paper,
            },
            '& .MuiDataGrid-virtualScrollerRenderZone': {
              backgroundColor: tokens.background.paper,
            },
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            color: tokens.text.secondary,
            fontSize: 14,
            '&.Mui-focused': {
              color: tokens.primary.main,
            },
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            color: tokens.text.primary,
            textAlign: 'left',
            fontSize: 12,
            marginLeft: 0,
            marginTop: 4,
          },
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          root: {
            marginRight: 16,
          },
          label: {
            fontSize: 14,
            color: tokens.text.secondary,
          },
        },
      },
      // Joy UI components (used in some edit cells)
      MuiSelect: {
        styleOverrides: {
          root: {
            color: isDark ? '#ffffff' :'#6c7f93',
            backgroundColor: isDark ? '#0c1f31' : '#f2f2f2',
            borderRadius: 8,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: isDark ? '#444c56' : '#ccc',
              borderWidth: '1px',
            },
            '&.Mui-focused': {
              outline: '2px solid transparent',
              outlineOffset: '0px',
              boxShadow: `0 0 0 2px ${tokens.background.default}, 0 0 0 4px ${tokens.ring}`,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: isDark ? '#444c56' : '#ccc', // Keep original border, don't change to teal
                borderWidth: '1px',
              },
            },
            '& .MuiSelect-select': {
              // minHeight: 40,
              paddingTop: 8,
              paddingBottom: 8,
              paddingLeft: 12,
              paddingRight: 12,
              fontSize: 14,
              lineHeight: '20px',
            },
          },
        },
      },      
      MuiInputBase: {
        styleOverrides: {
          root: {
            fontFamily: '"Inter", "Inter Fallback", Arial, sans-serif !important',
            color: isDark ? '#ffffff' :'#6c7f93',
            backgroundColor: isDark ? '#0c1f31' : '#f2f2f2',
            '& .MuiOutlinedInput-notchedOutline': {
              borderWidth: '1px',
            },
            '&.Mui-focused': {
              outline: '2px solid transparent',
              outlineOffset: '0px',
              boxShadow: `0 0 0 2px ${tokens.background.default}, 0 0 0 4px ${tokens.ring}`,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: isDark ? '#444c56' : '#ccc',
                borderWidth: '1px',
              },
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiInputBase-root': {
              color: isDark ? '#ffffff' :'#6c7f93',
              backgroundColor: isDark ? '#0c1f31' : '#f2f2f2',
            },
            '& .MuiInputLabel-root': {
              color: isDark ? '#b0b8c1' : '#2e394d',
            },
            '& .MuiOutlinedInput-root': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderWidth: '1px',
              },
              '&.Mui-focused': {
                outline: '2px solid transparent',
                outlineOffset: '0px',
                boxShadow: '0 0 0 2px #f2f2f2, 0 0 0 calc(2px + 2px) #009999',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: isDark ? '#444c56' : '#ccc',
                borderWidth: '1px',
              },
            },
            // Fix placeholder text color in dark mode
            '& .MuiInputBase-input::placeholder': {
              color: isDark ? '#6b7280' : '#9ca3af',
              opacity: 1,
            },
            '& .MuiInputBase-input::-webkit-input-placeholder': {
              color: isDark ? '#6b7280' : '#9ca3af',
              opacity: 1,
            },
            '& .MuiInputBase-input::-moz-placeholder': {
              color: isDark ? '#6b7280' : '#9ca3af',
              opacity: 1,
            },
            '& .MuiInputBase-input:-ms-input-placeholder': {
              color: isDark ? '#6b7280' : '#9ca3af',
              opacity: 1,
            },
            // Allow custom backgroundColor to override theme defaults
            '&[style*="background-color"], &[style*="backgroundColor"]': {
              '& .MuiInputBase-root': {
                backgroundColor: 'inherit',
              },
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'inherit',
              },
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? '#0c1f31' : '#f2f2f2',
            borderRadius: 8,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: isDark ? '#444c56' : '#ccc',
              borderWidth: '1px',
            },
            '&.Mui-focused': {
              outline: '2px solid transparent',
              outlineOffset: '0px',
              boxShadow: `0 0 0 2px ${tokens.background.default}, 0 0 0 4px ${tokens.ring}`,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: isDark ? '#444c56' : '#ccc', // Keep original border, don't change to teal
                borderWidth: '1px',
              },
            },
            '& .MuiInputBase-input': {
              paddingTop: 8,
              paddingBottom: 8,
              paddingLeft: 12,
              paddingRight: 12,
              fontSize: 14,
              lineHeight: '20px',
            },
            // Fix placeholder text color in dark mode
            '& .MuiInputBase-input::placeholder': {
              color: isDark ? '#6b7280' : '#9ca3af',
              opacity: 1,
            },
            '& .MuiInputBase-input::-webkit-input-placeholder': {
              color: isDark ? '#6b7280' : '#9ca3af',
              opacity: 1,
            },
            '& .MuiInputBase-input::-moz-placeholder': {
              color: isDark ? '#6b7280' : '#9ca3af',
              opacity: 1,
            },
            '& .MuiInputBase-input:-ms-input-placeholder': {
              color: isDark ? '#6b7280' : '#9ca3af',
              opacity: 1,
            },
            // Allow parent with inline styles to override background
            '&[style*="background-color"], &[style*="backgroundColor"]': {
              backgroundColor: 'inherit',
            },
          },
        },
      },
    },
    typography: {
      fontFamily: [
        "Inter",
        "Inter Fallback",
        // 'Roboto',
        // 'Montserrat',
        // '"Helvetica Neue"',
        'Arial',
        'sans-serif',
      ].join(','),
      fontSize: 14,
      ...BIDGELY_TYPOGRAPHY,
      // Heading color overrides
      h1: {
        color: isDark ? '#ffffff' : '#3d4d5c',
      },
      h2: {
        color: isDark ? '#ffffff' : '#3d4d5c',
      },
      h3: {
        color: isDark ? '#ffffff' : '#3d4d5c',
      },
      h4: {
        color: isDark ? '#ffffff' : '#3d4d5c',
        fontSize: '1.5rem',
        fontWeight: 700,
      },
      h5: {
        color: isDark ? '#ffffff' : '#3d4d5c',
      },
      h6: {
        color: isDark ? '#ffffff' : '#3d4d5c',
      },
      bglTitle1: {
        ...BIDGELY_TYPOGRAPHY.bglTitle1,
        color: isDark ? '#ffffff' : '#3d4d5c',
      },
      bglTitle2: {
        ...BIDGELY_TYPOGRAPHY.bglTitle2,
        color: isDark ? '#ffffff' : '#3d4d5c',
      },
      bglTitle3: {
        ...BIDGELY_TYPOGRAPHY.bglTitle3,
        color: isDark ? '#ffffff' : '#3d4d5c',
      },
      bglTitle4: {
        ...BIDGELY_TYPOGRAPHY.bglTitle4,
        color: isDark ? '#ffffff' : '#3d4d5c',
      },
    },
  });

  return theme
}
